import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import AuthModal from "@/components/feature/AuthModal";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import CompanyHeader from "./components/CompanyHeader";
import EmployeePanel from "./components/EmployeePanel";
import ProductPanel from "./components/ProductPanel";
import FinancePanel from "./components/FinancePanel";
import StockPanel from "./components/StockPanel";

type Tab = "overview" | "employees" | "products" | "finance" | "stock";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "overview",   label: "概要",       icon: "ri-dashboard-line" },
  { id: "employees",  label: "従業員",     icon: "ri-team-line" },
  { id: "products",   label: "製品・サービス", icon: "ri-box-3-line" },
  { id: "finance",    label: "財務",       icon: "ri-line-chart-line" },
  { id: "stock",      label: "株式",       icon: "ri-stock-line" },
];

export default function CompanyPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const { companies } = useGame();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  const company = companyId ? companies[companyId] : null;
  const isOwner = !!(user && company && company.ownerId === user.id);

  const pageBg = isDark ? "bg-[#0a0a0f]" : "bg-[#f8f8f8]";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const tabBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";

  if (!company) {
    return (
      <div className={`min-h-screen ${pageBg}`}>
        <Navbar />
        <div className="pt-20 flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-red-400/10 rounded-2xl mb-4">
            <i className="ri-building-4-line text-3xl text-red-400"></i>
          </div>
          <h2 className={`text-xl font-bold mb-2 ${textPrimary}`}>会社が見つかりません</h2>
          <p className={`text-sm mb-6 ${textSecondary}`}>この会社は存在しないか、削除された可能性があります</p>
          <button
            onClick={() => navigate("/mypage")}
            className="bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold rounded-full px-6 py-2.5 cursor-pointer whitespace-nowrap"
          >
            マイページへ戻る
          </button>
        </div>
        <AuthModal />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      <Navbar />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-6xl mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => navigate("/mypage")} className={`text-sm cursor-pointer hover:text-amber-400 transition-colors ${textSecondary}`}>
            マイページ
          </button>
          <i className="ri-arrow-right-s-line text-gray-500 text-sm"></i>
          <span className={`text-sm ${textPrimary}`}>{company.name}</span>
        </div>

        <CompanyHeader company={company} isOwner={isOwner} />

        {/* Tabs */}
        <div className={`rounded-2xl border mb-6 overflow-hidden ${tabBg}`}>
          <div className="flex overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 border-b-2 ${
                  activeTab === tab.id
                    ? "border-amber-400 text-amber-400"
                    : isDark
                    ? "border-transparent text-gray-400 hover:text-white"
                    : "border-transparent text-gray-500 hover:text-gray-900"
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
                {tab.id === "stock" && company.isPublic && (
                  <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-emerald-400/15 text-emerald-400 font-bold">上場</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <EmployeePanel company={company} isOwner={isOwner} />
            <ProductPanel company={company} isOwner={isOwner} />
          </div>
        )}
        {activeTab === "employees" && <EmployeePanel company={company} isOwner={isOwner} />}
        {activeTab === "products"  && <ProductPanel  company={company} isOwner={isOwner} />}
        {activeTab === "finance"   && <FinancePanel  company={company} />}
        {activeTab === "stock"     && <StockPanel    company={company} isOwner={isOwner} />}
      </div>
      <AuthModal />
    </div>
  );
}
